<script setup>
/**
 * AgentGoalDetails
 * Full-screen modal showing agent's client breakdown for a goal
 * Supports filtering, sorting, and expandable rows with item details
 */
import { ref, computed } from 'vue'
import { useAuthStore } from '@/app/store/auth'

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['close'])

const authStore = useAuthStore()

// Filters
const search = ref('')
const selectedDay = ref('')
const selectedWeek = ref('')
const clientsFilter = ref('הכל')

// Sorting
const sortConfig = ref({ column: '', ascending: true })

// Days and weeks options
const days = ['א', 'ב', 'ג', 'ד', 'ה']
const weeks = ['1', '2', '3', '4']
const filterOptions = ['הכל', 'לקוחות שסופקו', 'לקוחות שלא סופקו']

// Non-sortable headers (amount/total columns)
const nonSortableHeaders = ['כמות', 'סה"כ']

function isSortable(header) {
  return !nonSortableHeaders.includes(header)
}

// Get headers based on goal type
const headers = computed(() => {
  const goalType = props.data.goal?.type
  switch (goalType) {
    case 'כמותי':
      return ['מס לקוח', 'שם לקוח', 'עיר', 'כמות']
    case 'פיזור':
      return ['מס לקוח', 'שם לקוח', 'עיר', 'כמות']
    case 'כספי':
      return ['מס לקוח', 'שם לקוח', 'עיר', 'סה"כ']
    default:
      return []
  }
})

// Filter clients based on search, day, week, and indication
const filteredClients = computed(() => {
  let clients = []

  // Determine which clients to show
  if (clientsFilter.value === 'הכל') {
    clients = [
      ...(props.data.agent?.agents_clients || []),
      ...(props.data.agent?.missing_clients || []),
    ]
  } else if (clientsFilter.value === 'לקוחות שלא סופקו') {
    clients = props.data.agent?.missing_clients || []
  } else if (clientsFilter.value === 'לקוחות שסופקו') {
    clients = props.data.agent?.agents_clients || []
  }

  // Apply filters
  return clients.filter((client) => {
    // Search filter
    const searchMatch =
      !search.value ||
      client.makat?.includes(search.value) ||
      client.name?.includes(search.value) ||
      client.city?.includes(search.value)

    // Day filter
    const dayMatch = !selectedDay.value || client.visitation?.DAYNAME === selectedDay.value

    // Week filter
    const weekMatch =
      !selectedWeek.value || client?.visitation?.[`WEEK${selectedWeek.value}`] === 'Y'

    return searchMatch && dayMatch && weekMatch
  })
})

// Sort clients
const sortedClients = computed(() => {
  if (!sortConfig.value.column) return filteredClients.value

  const sorted = [...filteredClients.value]
  const { column, ascending } = sortConfig.value

  sorted.sort((a, b) => {
    let valA, valB

    switch (column) {
      case 'מס לקוח':
        valA = Number(a.makat) || 0
        valB = Number(b.makat) || 0
        break
      case 'שם לקוח':
        valA = a.name || ''
        valB = b.name || ''
        break
      case 'עיר':
        valA = a.city || ''
        valB = b.city || ''
        break
      default:
        return 0
    }

    if (valA < valB) return ascending ? -1 : 1
    if (valA > valB) return ascending ? 1 : -1
    return 0
  })

  return sorted
})

// Handle sort click
function handleSort(header) {
  if (!isSortable(header)) return

  if (sortConfig.value.column === header) {
    sortConfig.value.ascending = !sortConfig.value.ascending
  } else {
    sortConfig.value.column = header
    sortConfig.value.ascending = true
  }
}

// Calculate client total based on goal type
function getClientTotal(client) {
  const goalType = props.data.goal?.type
  const items = client.items || []

  if (goalType === 'כמותי') {
    return items.reduce((sum, item) => sum + (item.amount || 0), 0)
  } else if (goalType === 'פיזור') {
    return items.length
  } else if (goalType === 'כספי') {
    return items.reduce((sum, item) => sum + (item.total_cost || 0), 0).toFixed(2) + ' ₪'
  }
  return 0
}

// Product names are derived from the goal definition — they're no longer
// duplicated onto every stored line item / remained item, which keeps the goal
// document under Firestore's 1 MiB limit.
const makatNameMap = computed(() => {
  const map = {}
  for (const p of props.data.goal?.products || []) {
    map[p.makat] = p.name
  }
  return map
})

const goalMakats = computed(() => (props.data.goal?.products || []).map((p) => p.makat))

// Falls back to a stored name (older goal docs) then to the derived name.
function itemName(item) {
  return item.name || makatNameMap.value[item.makat] || item.makat
}

// Goal makats this client did not purchase — derived, not stored.
function getRemainedItems(client) {
  const supplied = new Set((client.items || []).map((i) => i.makat))
  return goalMakats.value
    .filter((makat) => !supplied.has(makat))
    .map((makat) => ({ makat, name: makatNameMap.value[makat] || makat }))
}

// Toggle expanded state for client
function toggleDetails(client) {
  client.show_details = !client.show_details
}

// Check if user can view this agent's data (claim-based)
const canView = computed(() => {
  const isAdmin = authStore.userRole === 'admin'
  const isOwnAgent = authStore.userAgentId === props.data.agent?.id
  return isAdmin || isOwnAgent
})
</script>

<template>
  <Teleport to="body">
    <div v-if="canView" class="agent-goal-details-overlay">
      <div class="agent-goal-details">
        <!-- Header -->
        <div class="details-header">
          <h2>{{ data.goal?.title }}</h2>
          <button class="close-btn" @click="emit('close')">
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <!-- Agent Info -->
        <div class="agent-info">
          <h3>
            {{ data.agent?.name }}
            <span v-if="data.goal?.type === 'כלכלי'">, לקוח - {{ data.agent?.client_makat }}</span>
          </h3>
        </div>

        <!-- Filters -->
        <div class="filters-row">
          <div class="filter-group">
            <input v-model="search" type="text" placeholder="חיפוש..." class="filter-input" />
          </div>

          <div class="filter-group">
            <select v-model="selectedDay" class="filter-select">
              <option value="">בחר יום</option>
              <option v-for="day in days" :key="day" :value="day">{{ day }}</option>
            </select>
          </div>

          <div class="filter-group">
            <select v-model="selectedWeek" class="filter-select">
              <option value="">בחר שבוע</option>
              <option v-for="week in weeks" :key="week" :value="week">{{ week }}</option>
            </select>
          </div>

          <div class="filter-group">
            <select v-model="clientsFilter" class="filter-select">
              <option v-for="option in filterOptions" :key="option" :value="option">
                {{ option }}
              </option>
            </select>
          </div>
        </div>

        <!-- Table -->
        <div class="table-container">
          <table v-if="['כמותי', 'פיזור', 'כספי'].includes(data.goal?.type)" class="clients-table">
            <thead>
              <tr>
                <th
                  v-for="header in headers"
                  :key="header"
                  :class="{ sortable: isSortable(header) }"
                  @click="handleSort(header)"
                >
                  <div class="th-content">
                    <svg
                      v-if="isSortable(header)"
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M7 15l5 5 5-5" />
                      <path d="M7 9l5-5 5 5" />
                    </svg>
                    <span>{{ header }}</span>
                  </div>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <template v-if="sortedClients.length === 0">
                <tr>
                  <td :colspan="headers.length + 1" class="no-records">לא קיימות רשומות</td>
                </tr>
              </template>

              <template v-for="client in sortedClients" :key="client.makat">
                <!-- Client Row -->
                <tr :class="{ 'missing-client': !client.items?.length }">
                  <td>{{ client.makat }}</td>
                  <td>{{ client.name }}</td>
                  <td>{{ client.city }}</td>
                  <td>{{ getClientTotal(client) }}</td>
                  <td class="toggle-cell">
                    <button
                      v-if="client.items?.length"
                      class="toggle-btn"
                      @click="toggleDetails(client)"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="20"
                        height="20"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        :class="{ rotated: client.show_details }"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  </td>
                </tr>

                <!-- Expanded Items Row -->
                <tr v-if="client.show_details" class="items-row">
                  <td :colspan="headers.length + 1">
                    <table class="items-table">
                      <thead>
                        <tr>
                          <th>מק"ט</th>
                          <th>שם מוצר</th>
                          <th>
                            {{ ['כמותי', 'פיזור'].includes(data.goal?.type) ? 'כמות' : 'סה"כ' }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <!-- Supplied Items -->
                        <tr v-for="item in client.items" :key="item.makat" class="supplied-item">
                          <td>{{ item.makat }}</td>
                          <td>{{ itemName(item) }}</td>
                          <td v-if="['כמותי', 'פיזור'].includes(data.goal?.type)">
                            {{ item.amount }}
                          </td>
                          <td v-else-if="data.goal?.type === 'כספי'">
                            {{ item.total_cost?.toFixed(2) }} ₪
                          </td>
                        </tr>
                        <!-- Remaining Items -->
                        <tr
                          v-for="item in getRemainedItems(client)"
                          :key="item.makat"
                          class="missing-item"
                        >
                          <td>{{ item.makat }}</td>
                          <td>{{ item.name }}</td>
                          <td v-if="['כמותי', 'פיזור'].includes(data.goal?.type)">0</td>
                          <td v-else-if="data.goal?.type === 'כספי'">0 ₪</td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.agent-goal-details-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--bg-primary);
  z-index: 1000;
  overflow: hidden;
}

.agent-goal-details {
  width: 100%;
  max-width: 1200px;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  color: var(--text-primary);
}

/* Header */
.details-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
}

.details-header h2 {
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
}

.close-btn {
  position: absolute;
  left: var(--spacing-md);
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  padding: var(--spacing-xs);
  border-radius: var(--border-radius);
  transition: all var(--transition-fast);
}

.close-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

/* Agent Info */
.agent-info {
  padding: var(--spacing-md);
  text-align: center;
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
}

.agent-info h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
}

/* Filters */
.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);
}

.filter-group {
  flex: 1;
  min-width: 150px;
}

.filter-input,
.filter-select {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
}

.filter-input:focus,
.filter-select:focus {
  outline: none;
  border-color: var(--color-primary);
}

/* Table Container */
.table-container {
  flex: 1;
  overflow: auto;
  padding: var(--spacing-md);
}

/* Clients Table */
.clients-table {
  width: 100%;
  border-collapse: collapse;
}

.clients-table th,
.clients-table td {
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  text-align: center;
}

.clients-table th {
  background: var(--color-success);
  color: white;
  font-weight: var(--font-weight-semibold);
  position: sticky;
  top: 0;
  z-index: 1;
}

.clients-table th.sortable {
  cursor: pointer;
}

.clients-table th.sortable:hover {
  background: #03945f;
}

.th-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
}

.clients-table tbody tr:nth-child(even) {
  background: var(--bg-secondary);
}

.clients-table tbody tr:nth-child(odd) {
  background: var(--bg-primary);
}

.clients-table tbody tr:hover {
  background: var(--bg-tertiary);
}

.missing-client {
  color: var(--color-error);
}

.toggle-cell {
  width: 40px;
}

.toggle-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-primary);
  padding: var(--spacing-xs);
}

.toggle-btn svg {
  transition: transform var(--transition-fast);
}

.toggle-btn svg.rotated {
  transform: rotate(180deg);
}

.no-records {
  padding: var(--spacing-xl) !important;
  text-align: center;
  color: var(--text-muted);
  font-size: var(--font-size-lg);
}

/* Items Table */
.items-row > td {
  padding: 0 !important;
  background: var(--bg-tertiary);
}

.items-table {
  width: 100%;
  border-collapse: collapse;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.1);
}

.items-table th,
.items-table td {
  padding: var(--spacing-xs) var(--spacing-sm);
  border: 1px solid var(--border-color);
  text-align: center;
}

.items-table th {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-sm);
}

.supplied-item {
  color: var(--color-success);
}

.missing-item {
  color: var(--color-error);
}

/* Responsive */
@media (max-width: 768px) {
  .filters-row {
    flex-direction: column;
  }

  .filter-group {
    width: 100%;
  }

  .clients-table th,
  .clients-table td {
    padding: var(--spacing-xs);
    font-size: var(--font-size-sm);
  }
}
</style>
